import { useState, useMemo, useRef } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Download, Mail, CheckCircle, Trash2, FileText, Loader2, Eye } from "lucide-react";
import { useInvoices, nextInvoiceNumber, type Invoice, type InvoiceInput } from "@/hooks/useInvoices";
import InvoiceForm from "@/components/invoice/InvoiceForm";
import InvoicePDFView from "@/components/invoice/InvoicePDFView";
import { exportProposalPDF } from "@/lib/exportProposalPDF";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useSearchParams } from "@/lib/router-compat";
import { useEffect } from "react";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const statusColor: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-blue-500/20 text-blue-300",
  paid: "bg-emerald-500/20 text-emerald-300",
  overdue: "bg-red-500/20 text-red-300",
  void: "bg-zinc-600 text-zinc-200",
};

export default function Invoices() {
  const { invoices, isLoading, saveInvoice, deleteInvoice, markPaid, markSent } = useInvoices();
  const { settings: companySettings } = useCompanySettings();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [previewing, setPreviewing] = useState<Invoice | null>(null);
  const [viewing, setViewing] = useState<Invoice | null>(null);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillRef = useRef<Partial<InvoiceInput> | null>(null);

  // Handle prefill from sessionStorage (from proposal -> invoice)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const raw = sessionStorage.getItem("invoice_prefill");
      if (raw) {
        try { prefillRef.current = JSON.parse(raw); } catch { /* noop */ }
        sessionStorage.removeItem("invoice_prefill");
      }
      setShowForm(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const editing = useMemo(() => invoices.find((i) => i.id === editingId), [invoices, editingId]);
  const invNumber = useMemo(() => nextInvoiceNumber(invoices), [invoices]);

  const handleSubmit = async (data: InvoiceInput) => {
    setSaving(true);
    const result = await saveInvoice(editingId ? { ...data, id: editingId } : data);
    setSaving(false);
    if (result) {
      toast({ title: editingId ? "Invoice updated" : "Invoice created" });
      setShowForm(false);
      setEditingId(null);
      prefillRef.current = null;
    }
  };

  const handleDownload = async (inv: Invoice) => {
    setPreviewing(inv);
    await new Promise((r) => setTimeout(r, 250));
    try {
      await exportProposalPDF({
        elementId: "invoice-pdf-content",
        filenamePrefix: `invoice-${inv.invoice_number}`,
        industryLabel: `Invoice ${inv.invoice_number}`,
      });
    } catch (e) {
      toast({ title: "PDF export failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setPreviewing(null);
    }
  };

  const handleSend = async (inv: Invoice) => {
    if (!inv.client_email) {
      toast({ title: "Missing client email", description: "Add a client email first.", variant: "destructive" });
      return;
    }
    setSendingId(inv.id);
    try {
      const { error } = await supabase.functions.invoke("send-invoice", { body: { invoice_id: inv.id } });
      if (error) throw error;
      await markSent(inv.id);
      toast({ title: "Invoice sent", description: `Emailed to ${inv.client_email}` });
    } catch (e) {
      toast({ title: "Send failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSendingId(null);
    }
  };

  return (
    <AppLayout>
      <div className="container max-w-6xl py-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="h-6 w-6 text-primary" />Invoices</h1>
            <p className="text-sm text-muted-foreground">Create, send, and track invoices</p>
          </div>
          <Button onClick={() => { setEditingId(null); prefillRef.current = null; setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-1" /> New Invoice
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : invoices.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No invoices yet. Click "New Invoice" to create one.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => (
              <Card key={inv.id}>
                <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{inv.invoice_number}</span>
                      <Badge className={statusColor[inv.status]}>{inv.status.toUpperCase()}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{inv.client_name}{inv.client_company ? ` — ${inv.client_company}` : ""}</p>
                    <p className="text-xs text-muted-foreground">Issued {inv.issue_date}{inv.due_date ? ` · Due ${inv.due_date}` : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">{fmt(inv.total)}</p>
                    <div className="flex gap-1 flex-wrap justify-end mt-1">
                      <Button size="sm" variant="outline" onClick={() => { setEditingId(inv.id); setShowForm(true); }}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => setViewing(inv)} title="Preview"><Eye className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleDownload(inv)} title="Download PDF"><Download className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleSend(inv)} disabled={sendingId === inv.id}>
                        {sendingId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                      </Button>
                      {inv.status !== "paid" && (
                        <Button size="sm" variant="outline" onClick={() => markPaid(inv.id, inv.total)}>
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm("Delete this invoice?")) deleteInvoice(inv.id); }}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditingId(null); prefillRef.current = null; } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit Invoice" : "New Invoice"}</DialogTitle></DialogHeader>
          <InvoiceForm
            initial={editing ?? { payment_terms: companySettings.default_payment_terms, ...(prefillRef.current ?? {}) }}
            invoiceNumber={editing?.invoice_number ?? invNumber}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setEditingId(null); prefillRef.current = null; }}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 pb-0"><DialogTitle>Invoice Preview</DialogTitle></DialogHeader>
          {viewing && (
            <div className="p-2">
              <InvoicePDFView invoice={viewing} />
            </div>
          )}
          {viewing && (
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
              <Button onClick={() => { const inv = viewing; setViewing(null); handleDownload(inv); }}>
                <Download className="h-4 w-4 mr-1" /> Download PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden PDF render target for download */}
      {previewing && (
        <div style={{ position: "fixed", left: -10000, top: 0, width: 900 }}>
          <InvoicePDFView invoice={previewing} />
        </div>
      )}
    </AppLayout>
  );
}
